import React from 'react';
import NextStepsFallback, { NextStepsEmpty } from './nextStepsFallback';

/*
  Next Steps panel
  ----------------
  Renders the admin-authored copy that getNextSteps returns for a DJ's status.
  The HTML arrives already sanitized by the backend (libs/richText.js); this
  file owns the two things the backend deliberately doesn't:

    - Presentation. Quickbase supplies a tone vocabulary word, never a class
      name - Tailwind only generates CSS for classes it finds while scanning
      ./src, so a class typed into Quickbase would produce no styling at all.

    - Per-DJ values. {{merge_tokens}} resolve here rather than server-side,
      because /api has no auth and the response is cached whole-table for every
      DJ at once. Nothing DJ-specific may go into that cache.
*/

/*
  An unrecognized or blank tone clamps to Neutral, the same way widthFor() clamps
  a width a widget can't support.

  The tone styles the Label and nothing else. That is how production renders:
  the colored callout is a box around the header line, and the body under it -
  the numbered list, the follow-up paragraphs - sits outside that box on the
  card's own white background. Putting the tone on the wrapper instead tinted
  the whole widget, which is the difference this table exists to undo.

  Padding and rounding therefore travel with the colored tones. Neutral adds
  nothing at all - its label inherits the panel's own text color and is simply
  bold, because an unboxed header is a bold line, not an untinted box.

  The box is p-2, not the p-4 production uses. A header is one or two lines, and
  16px around a single line reads as a gap rather than a box - the text sits
  optically high in it, because the line box carries descender space under the
  glyphs that no padding value evens out. 8px sits closer to the text.

  Quickbase spells its choices "Success - Green", "Info - Yellow" and "Neutral -
  None" so an admin picking one can see what it will look like. Only the word
  before the dash selects anything here, which is also what keeps rows written
  before the rename - they still hold a bare "Success"/"Info"/"Neutral" - from
  falling through to the clamp.

  There is no Warning any more. It was red, it existed for the rejection notice,
  and both the choice and the only row using it are gone from Quickbase.
*/
const TONES = {
  Success: {className: 'bg-green-50 text-green-800 p-2 rounded-lg', boxed: true},
  Info: {className: 'bg-yellow-50 text-yellow-800 p-2 rounded-lg', boxed: true},
  Neutral: {className: '', boxed: false},
};

function toneFor(tone) {
  const choice = String(tone ?? '').split('-')[0].trim();
  if (!choice) return TONES.Neutral;
  if (!TONES[choice]) {
    // Blank is a normal row; a value nobody recognizes is a retired choice or a
    // typo, and clamping it silently is how the panel would go grey unexplained.
    console.warn(`Unknown Next Steps tone: "${tone}"`);
    return TONES.Neutral;
  }
  return TONES[choice];
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => (
    {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]
  ));
}

// Only http(s) reaches an href. A Quickbase URL field holds whatever someone
// typed, and this value is substituted after sanitization, so it doesn't get a
// second look from sanitize-html.
function safeUrl(url) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : '';
}

// Merge tokens are for text. Anything that should become a link is an anchor
// instead - see DYNAMIC_LINKS below.
function tokenValues({election, dj}) {
  return {
    election_name: escapeHtml(election?.name),
    election_date: escapeHtml(election?.election_date),
    first_name: escapeHtml(dj?.first_name),
  };
}

/*
  Anchors whose href names a value rather than a place on the page.

  Authors write these exactly like the widget links they already use - insert a
  link with the toolbar, type #sosha_link as the address - and the real URL is
  filled in here from the DJ record. One mechanism instead of two: "links start
  with #" is the whole rule, rather than that plus a merge token that emits its
  own <a> and owns the wording inside it.

  A key that is not in this table is left alone. Those are the widget anchors,
  and handleAnchorClick turns them into a scroll.
*/
const DYNAMIC_LINKS = {
  sosha_link: (dj) => dj?.sosha_link,
};

/*
  sanitize-html normalizes every surviving anchor to <a href="..."> with double
  quotes and nothing else on it - transformTags rebuilds the attribs from
  scratch - so the shape matched here is one this app emitted, not whatever an
  author pasted. Anchors cannot nest, so stopping at the first </a> is safe.

  A DJ whose record has no value for the key gets the whole anchor dropped - the
  link and the words inside it. There is no substitute text, so an author has to
  write the link somewhere its absence leaves a sentence that still works; the
  guide field on the Quickbase table says so and shows the shape to use.

  Substitute text is what this deliberately does not do. The mechanism it
  replaces put "the Post to social button at the top of the screen" in the gap,
  which was wrong on its own terms: Header.js renders that button only when
  dj.sosha_link exists, so the fallback named a button that was absent in exactly
  the case it appeared.
*/
function resolveDynamicLinks(html, dj) {
  let dropped = false;
  const resolved = html.replace(/<a href="#([\w-]+)">([\s\S]*?)<\/a>/gi, (match, key, text) => {
    const resolve = DYNAMIC_LINKS[key.toLowerCase()];
    if (!resolve) return match;
    const value = resolve(dj);
    const url = safeUrl(value);
    if (!url) {
      /*
        Two different things end up here and only one of them is normal.

        No value at all is the documented case - a DJ without a Sosha page - and
        stays quiet, because it is every DJ before Contract Signed and warning
        would fire constantly.

        A value safeUrl rejected is a data error: the field holds something, and
        the DJ silently loses a sentence over it. That is the same class of
        problem the backend already warns about when the sanitizer empties a
        field, and it is worth saying out loud because nothing else on the page
        does. Header.js renders the Post to social button off the raw value with
        no scheme check, so a record like "www.sosha..." shows the button and
        drops this link - which reads as "the link isn't parsing" and points
        nowhere near the actual cause.
      */
      if (value) {
        console.warn(`Next Steps link #${key}: "${value}" is not an http(s) URL, so the link was dropped`);
      }
      dropped = true;
      return '';
    }
    return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-primary-dark underline">${text}</a>`;
  });

  // An author who gave the link a bullet or a paragraph of its own - which is
  // what the guide asks for, because it is the shape that survives the link
  // going away - leaves an empty one behind when it does. A blank bullet is
  // still something on the DJ's screen, so it goes too. Only runs when a link
  // was actually dropped: an empty <p> an author typed for spacing is theirs.
  return dropped ? resolved.replace(/<(li|p|h[1-6])>\s*<\/\1>/gi, '') : resolved;
}

export function resolveTokens(html, context) {
  if (typeof html !== 'string' || !html) return '';
  const values = tokenValues(context);
  const withTokens = html.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (match, name) => {
    const key = name.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(values, key)) return values[key];
    // Dropped rather than printed - a typo in Quickbase shouldn't put
    // "{{sosha_lnk}}" in front of a DJ.
    console.warn(`Unknown Next Steps merge token: {{${name}}}`);
    return '';
  });
  return resolveDynamicLinks(withTokens, context.dj);
}

// Authors write <a href="#contract-section"> to point at another widget. Real
// hash navigation would change the URL and jump, so it's intercepted here.
function handleAnchorClick(event) {
  const anchor = event.target.closest?.('a[href^="#"]');
  if (!anchor) return;
  event.preventDefault();
  const target = document.getElementById(anchor.getAttribute('href').slice(1));
  // No target is a silent no-op on purpose: widget visibility is Quickbase
  // driven, so the widget an author linked to may not be on the page for this
  // DJ's status. The hardcoded button this replaces null-checked the same way.
  if (target) target.scrollIntoView({behavior: 'smooth'});
}

const NextStepsPanel = ({ nextSteps, election, dj }) => {
  const status = election?.dj_elections?.[0]?.election_status;

  // Still in flight. The dashboard deliberately does not block on this request,
  // so render nothing rather than flashing the empty state - the card chrome is
  // already on screen.
  if (nextSteps === null || nextSteps === undefined) return null;

  // The request failed. Fall back to the copy baked into this build rather than
  // showing a DJ an empty panel during a Quickbase outage.
  if (nextSteps === 'error') {
    return <NextStepsFallback status={status} dj={dj} />;
  }

  // Keyed by status, not by election rid: Quickbase has one row per status now,
  // and the election in scope only decides what {{election_date}} resolves to.
  const row = nextSteps[status];
  if (!row) return <NextStepsEmpty />;

  const label = resolveTokens(row.label, {election, dj});
  const content = resolveTokens(row.content, {election, dj});
  if (!label && !content) return <NextStepsEmpty />;

  /*
    Label and body are separate fields in Quickbase and stay separate elements
    here, so the header can be styled as one without an author having to
    remember to bold it - and so the tone can color the header alone.

    Both divs clear their own outer margins. Wrapping them costs each one its
    `prose > :first-child` match, so without this the label's paragraph would
    open the callout with a blank line and the body would push a margin past
    the panel's last child.

    The gap under a boxed label is the space between the callout and the body
    that follows it, which is production's space-y-4. An unboxed Neutral label
    is just a bold line, so it gets the tighter mt-2 it always had.

    next-steps-body is defined in index.css, not here: it rebuilds production's
    circled-number steps out of the bare <ol> Quickbase sends, which takes
    ::before and a counter that Tailwind utilities can't express inline.

    text-base on the label is what makes the header 16px/24px. The panel is
    prose-sm, so without it the header would inherit the body's 14px - and
    production's header is a size up from its body text, not the same size in a
    heavier weight. font-medium and the tone's -800 text match production too;
    the -900 and font-semibold they replace were this branch's invention.

    Production's header also carries mb-2, which lands 24px under the text and
    16px over it inside the same p-4 box. That is a stray margin on a last
    child, not a design, so [&>*:last-child]:mb-0 keeps the box even here.
  */
  const tone = toneFor(row.tone);

  return (
    <div
      onClick={handleAnchorClick}
      className="prose prose-sm max-w-none prose-a:text-primary text-gray-600"
    >
      {label && (
        <div
          className={`text-base font-medium [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${tone.className}`}
          dangerouslySetInnerHTML={{__html: label}}
        />
      )}
      {content && (
        <div
          className={`next-steps-body [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${label ? (tone.boxed ? 'mt-4' : 'mt-2') : ''}`}
          dangerouslySetInnerHTML={{__html: content}}
        />
      )}
    </div>
  );
};

export default NextStepsPanel;
